import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  useTheme,
  TextField,
  Select,
  MenuItem,
  Typography,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { useColourMode } from "../contexts/ColourContext";
import SearchIcon from "@mui/icons-material/Search";
import { categoryTypes } from "../data/database";
import { formatNumber } from "../functions";

// Utility function to capitalize first letter of each word
const titleCase = (str) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const Product = ({
  userId,
  productId,
  product,
  price,
  category,
  type,
  dataUrl,
  types = {},
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCustomType, setIsCustomType] = useState(false);
  const [editedProduct, setEditedProduct] = useState({
    product: product || "",
    price: price || "",
    category: category || "",
    type: type || "",
  });

  const colourTheme = useTheme();
  const { accentColour } = useColourMode();

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") {
      setEditedProduct((prev) => ({
        ...prev,
        category: value,
        type: "", // Reset type when category changes
      }));
      setIsCustomType(false); // Reset custom type when category changes
    } else {
      setEditedProduct((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleTypeChange = (e) => {
    const { value } = e.target;
    if (value === "Other") {
      setIsCustomType(true);
      setEditedProduct((prev) => ({ ...prev, type: "" }));
    } else {
      setIsCustomType(false);
      setEditedProduct((prev) => ({ ...prev, type: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      const productData = {
        userId,
        category: editedProduct.category,
        type: editedProduct.type,
        product: editedProduct.product,
        price: editedProduct.price,
      };

      await axios.put(
        `${dataUrl}/products/${userId}/${productId}`,
        productData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setIsExpanded(false);
      window.location.reload();
    } catch (error) {
      console.error(
        "Error updating data:",
        error.response?.data || error.message
      );
      alert("Failed to update data. Please try again.");
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("Authorization token is missing.");
        return;
      }

      const confirmed = window.confirm(
        "Are you sure you want to delete this product?"
      );
      if (confirmed) {
        await axios.delete(`${dataUrl}/products/${userId}/${productId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        alert("Product deleted successfully!");
        setIsExpanded(false);
      }
      window.location.reload();
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  return (
    <Box
      onClick={toggleExpand}
      sx={{
        cursor: "pointer",
        margin: "0.75rem",
        borderBottom: `1px solid ${colourTheme.palette.divider}`,
        pb: 1,
      }}
    >
      {/* Product Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body1">{product}</Typography>
        {!isExpanded && <Typography variant="body1">{price}</Typography>}
        {isExpanded && (
          <Button
            variant="contained"
            color="error"
            size="small"
            sx={{
              fontSize: "0.65rem",
              textTransform: "uppercase",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
          >
            Delete
          </Button>
        )}
      </Box>

      {/* Expanded Form */}
      {isExpanded && (
        <Box
          component="form"
          onSubmit={handleSubmit}
          onClick={(e) => e.stopPropagation()}
          sx={{ mt: 2 }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Category */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ width: "25%" }}>
                Category
              </Typography>
              <Select
                name="category"
                value={editedProduct.category}
                onChange={handleInputChange}
                required
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colourTheme.palette.text.primary,
                  },
                }}
              >
                {Object.keys(categoryTypes).map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {titleCase(cat)}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Type */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ width: "25%" }}>
                Type
              </Typography>
              {isCustomType ? (
                <Select
                  name="type"
                  value={editedProduct.type}
                  onChange={handleTypeChange}
                  required
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colourTheme.palette.text.primary,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Type
                  </MenuItem>
                  {editedProduct.category &&
                    types[editedProduct.category]?.map((type, typeIndex) => (
                      <MenuItem key={typeIndex} value={type}>
                        {titleCase(type)}
                      </MenuItem>
                    ))}
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              ) : (
                <TextField
                  name="type"
                  placeholder="Enter New Type"
                  value={editedProduct.type}
                  onChange={handleInputChange}
                  required
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: colourTheme.palette.text.primary,
                      },
                    },
                  }}
                />
              )}
            </Box>

            {/* Product Name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ width: "25%" }}>
                Product
              </Typography>
              <TextField
                name="product"
                value={editedProduct.product}
                onChange={handleInputChange}
                placeholder="Product"
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colourTheme.palette.text.primary,
                    },
                  },
                }}
              />
            </Box>

            {/* Price */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ width: "25%" }}>
                Price
              </Typography>
              <TextField
                name="price"
                type="number"
                value={editedProduct.price}
                onChange={handleInputChange}
                placeholder="Price"
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: colourTheme.palette.text.primary,
                    },
                  },
                }}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="small"
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                size="small"
                onClick={toggleExpand}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const ManageProduct = ({ userId, header, dataUrl, types }) => {
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [searchProducts, setSearchProducts] = useState("");
  const [error, setError] = useState("");

  const colourTheme = useTheme();
  const { accentColour } = useColourMode();

  const handleSearchChange = (e) => {
    setSearchProducts(e.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          setError("Authorization token is missing.");
          return;
        }

        if (productsLoaded) {
          return;
        }

        const response = await axios.get(`${dataUrl}/products`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const sortedData = response.data.sort((a, b) =>
          a.product.localeCompare(b.product)
        );
        setProducts(sortedData);
        setProductsLoaded(true);
      } catch (error) {
        console.error("Error fetching users data:", error);
        const message =
          error.response && error.response.status === 403
            ? "You do not have the necessary permissions."
            : "Failed to fetch product data. Please try again later.";
        setError(message);
      }
    };

    fetchData();
  }, [productsLoaded]);

  const filteredProducts = products?.filter(
    (product) =>
      product.product?.toLowerCase().includes(searchProducts.toLowerCase()) ||
      product.category?.toLowerCase().includes(searchProducts.toLowerCase()) ||
      product.type?.toLowerCase().includes(searchProducts.toLowerCase())
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">{header}</Typography>
        <Typography variant="body2">
          {formatNumber(filteredProducts?.length)}
        </Typography>
      </Box>

      <Box
        sx={{
          backdropFilter: "blur(1rem)",
          boxShadow: "0 0 1rem rgba(0, 0, 0, 0.2)",
          width: "100%",
          border: `1px solid ${colourTheme.palette.text.primary}`,
          borderRadius: "1rem",
          m: "auto",
          mb: 2.5,
          p: 2,
        }}
      >
        {/* Search */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search Products..."
          value={searchProducts}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{
                  color: colourTheme.palette.text.secondary,
                  mr: 1,
                }}
              />
            ),
            sx: {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colourTheme.palette.text.primary,
              },
            },
          }}
          sx={{ mb: 2 }}
        />

        {/* Product List */}
        <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <Product
                key={product.id}
                product={product.product}
                productId={product.productId}
                price={product.price}
                category={product.category}
                type={product.type}
                userId={userId}
                dataUrl={dataUrl}
                types={types}
              />
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                mt: 2,
                color: colourTheme.palette.text.secondary,
                fontStyle: "italic",
              }}
            >
              No products found
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ManageProduct;
